import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { StyledConsignedCard, StyledHeader, StyledTitle } from './styles';
import { formatCurrency, normalizeCpfCnpj } from 'app/utils/normalizer';
import ConsignedCard from '../ConsignedCard/ConsignedCard';
import { useContext, useState } from 'react';
import { ApplicationRequestContext } from '../../ApplicationRequest';
import { usePersonContext } from 'modules/customer/context';
import { TextL, TextM } from 'app/components';
import dayjs from 'dayjs';

type TSimulationComponentProps = {
  isCalcSimulation?: boolean;
};

const SimulationComponent = ({
  isCalcSimulation,
}: TSimulationComponentProps) => {
  // Hooks
  const { person } = usePersonContext();
  const {
    consignedProduct,
    setConsignedProduct,
    consignedList,
    setConsignedConfig,
  } = useContext(ApplicationRequestContext);

  // States
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenHistory, setIsOpenHistory] = useState(false);

  // Variables
  const legacyConsigned = consignedList?.links.flatMap(
    (item) => item.meta_data?.legacy_loans ?? [],
  );
  const hasLegacy = !!legacyConsigned?.length;

  const renderLegacy = () => {
    return legacyConsigned?.map((legacy) => (
      <>
        <TableRow key={legacy?.contract}>
          <TableCell colSpan={4}>
            <br />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Institução financeira</TextM>
          </TableCell>
          <TableCell colSpan={1} style={{ borderRight: ' 1px solid #E0E0E0' }}>
            <TextM>{legacy?.financial_institution_code || '-'}</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Número do contrato</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM>{legacy?.contract || '-'}</TextM>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Valor do empréstimo</TextM>
          </TableCell>
          <TableCell colSpan={1} style={{ borderRight: ' 1px solid #E0E0E0' }}>
            <TextM>{formatCurrency(legacy?.loan_amount) || '-'}</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Valor solicitado</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM>{formatCurrency(legacy?.net_amount) || '-'}</TextM>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Taxa mensal</TextM>
          </TableCell>
          <TableCell colSpan={1} style={{ borderRight: ' 1px solid #E0E0E0' }}>
            <TextM>{legacy?.interest_tax || '-'}%</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Valor da CET mensal</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM>{legacy?.monthly_cet || '-'}</TextM>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Quantidade de parcelas pagas</TextM>
          </TableCell>
          <TableCell colSpan={1} style={{ borderRight: ' 1px solid #E0E0E0' }}>
            <TextM>{legacy?.paid_installments_quantity || '-'}</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM weight="Bold">Valor saldo devedor</TextM>
          </TableCell>
          <TableCell colSpan={1}>
            <TextM>
              {formatCurrency(legacy?.debtor_balance_amount) || '-'}
            </TextM>
          </TableCell>
        </TableRow>
      </>
    ));
  };

  return (
    <>
      <StyledConsignedCard>
        <StyledHeader>
          <StyledTitle>Resultado da consulta</StyledTitle>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2 }}
            style={{ marginTop: '1rem', padding: '0' }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ padding: '1rem' }}
            >
              <TextL weight="bold">Dados do Tomador</TextL>
              <IconButton
                size="small"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                {isOpen ? (
                  <KeyboardArrowUpIcon color="primary" />
                ) : (
                  <KeyboardArrowDownIcon color="primary" />
                )}
              </IconButton>
            </Box>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <TableContainer style={{ marginTop: '1rem' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <TextM weight="Bold">Nome</TextM>
                      </TableCell>
                      <TableCell style={{ borderRight: ' 1px solid #E0E0E0' }}>
                        <TextM>
                          {person?.full_name || 'Não disponibilizado.'}
                        </TextM>
                      </TableCell>
                      <TableCell>
                        <TextM weight="Bold">CPF</TextM>
                      </TableCell>
                      <TableCell>
                        <TextM>
                          {person?.taxpayer_id
                            ? normalizeCpfCnpj(person?.taxpayer_id)
                            : 'Não disponibilizado.'}
                        </TextM>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <TextM weight="Bold">Sexo</TextM>
                      </TableCell>
                      <TableCell style={{ borderRight: ' 1px solid #E0E0E0' }}>
                        <TextM>{person?.sex?.label || '-'}</TextM>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <TextM weight="Bold">Data de nascimento</TextM>
                      </TableCell>
                      <TableCell>
                        <TextM>
                          {dayjs(person?.birth_date).format('DD/MM/YYYY') ||
                            '-'}
                        </TextM>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <TextM weight="Bold">Nome da mãe</TextM>
                      </TableCell>
                      <TableCell style={{ borderRight: ' 1px solid #E0E0E0' }}>
                        <TextM>{person?.mothers_name || '-'}</TextM>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <TextM weight="Bold">Nacionalidade</TextM>
                      </TableCell>
                      <TableCell>
                        <TextM>{person?.nationality || '-'}</TextM>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={1}
                        style={{
                          borderBottom: 'none',
                        }}
                      >
                        <TextM weight="Bold">PEP</TextM>
                      </TableCell>
                      <TableCell
                        style={{
                          borderRight: ' 1px solid #E0E0E0',
                          borderBottom: 'none',
                        }}
                      >
                        <TextM>{person.pep?.label || '-'}</TextM>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>

          {hasLegacy && (
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2, p: 2 }}
              style={{ marginTop: '1rem', padding: '0' }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                style={{ padding: '1rem' }}
              >
                <TextL weight="bold">Histórico de crédito</TextL>
                <IconButton
                  size="small"
                  onClick={() => setIsOpenHistory((prev) => !prev)}
                >
                  {isOpen ? (
                    <KeyboardArrowUpIcon color="primary" />
                  ) : (
                    <KeyboardArrowDownIcon color="primary" />
                  )}
                </IconButton>
              </Box>

              <Collapse in={isOpenHistory} timeout="auto" unmountOnExit>
                <TableContainer style={{ marginTop: '1rem' }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={1}>
                          <TextM weight="Bold">
                            Empréstimos ativos suspensos
                          </TextM>
                        </TableCell>
                        <TableCell
                          colSpan={1}
                          style={{ borderRight: ' 1px solid #E0E0E0' }}
                        >
                          <TextM>0</TextM>
                        </TableCell>
                        <TableCell colSpan={1}>
                          <TextM weight="Bold">Empréstimos legados</TextM>
                        </TableCell>
                        <TableCell colSpan={1}>
                          <TextM>{legacyConsigned?.length}</TextM>
                        </TableCell>
                      </TableRow>

                      {renderLegacy()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          )}
        </StyledHeader>
        <Divider style={{ margin: '1.5rem 1rem' }} />

        <StyledTitle style={{ marginLeft: '1rem' }}>
          Selecione a oferta de crédito:
        </StyledTitle>

        {consignedList?.links?.map((item, key) => (
          <ConsignedCard
            consigned={item}
            key={key}
            setConsignedConfig={setConsignedConfig}
            setConsignedProduct={setConsignedProduct}
            consignedProduct={consignedProduct}
            isCalcSimulation={isCalcSimulation}
          />
        ))}
      </StyledConsignedCard>
    </>
  );
};

export default SimulationComponent;
